import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Volleyball, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('No confirmation token provided. Please check your email link.');
        return;
      }

      try {
        // Call the confirm-email edge function
        const { data, error } = await supabase.functions.invoke('confirm-email', {
          body: { token }
        });

        if (error) {
          console.error('Error confirming email:', error);
          setStatus('error');
          setMessage('Failed to confirm your email. Please try again.');
          return;
        }

        if (data?.success) {
          setStatus('success');
          setMessage(data.message || 'Your email has been confirmed successfully!');
        } else {
          setStatus('error');
          setMessage(data?.message || 'Failed to confirm your email. Please try again.');
        }
        toast.success('Email confirmed! You can now sign in.');

        // Redirect to sign in after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);

      } catch (error) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    confirmEmail();
  }, [searchParams, navigate]);

  const handleGoToSignIn = () => {
    navigate('/auth');
  };

  const handleResendConfirmation = () => {
    navigate('/auth?mode=signup');
    toast.info('Please sign up again to receive a new confirmation email.');
  };

  return (
    <div className="min-h-dvh bg-cover bg-center bg-no-repeat flex items-center justify-center p-2 sm:p-4 relative overflow-hidden" style={{backgroundImage: 'url(/lovable-uploads/37f167c2-5c4f-4873-8fe8-48c70f19c890.png)'}}>
      <div className="absolute inset-0 bg-black/40"></div>
      <Card className="w-full max-w-md relative z-10 backdrop-blur-sm bg-card/95">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-blue-400 rounded-full flex items-center justify-center">
              <Volleyball className="w-6 h-6 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Email Confirmation
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
              <p className="text-muted-foreground">
                Confirming your email address...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <div className="space-y-2">
                <p className="text-lg font-semibold text-green-700">
                  Success!
                </p>
                <p className="text-muted-foreground">
                  {message}
                </p>
                <p className="text-sm text-muted-foreground">
                  You'll be redirected to sign in automatically, or click the button below.
                </p>
              </div>
              <Button onClick={handleGoToSignIn} className="w-full">
                Go to Sign In
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <div className="space-y-2">
                <p className="text-lg font-semibold text-red-700">
                  Confirmation Failed
                </p>
                <p className="text-muted-foreground">
                  {message}
                </p>
              </div>
              <div className="space-y-2">
                <Button onClick={handleGoToSignIn} className="w-full">
                  Go to Sign In
                </Button>
                <Button 
                  onClick={handleResendConfirmation} 
                  variant="outline" 
                  className="w-full"
                >
                  Request New Confirmation Email
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailConfirmation;